import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import {
  Companies,
  CompaniesUpdateInput,
  CompaniesInsertInput,
} from "@/src/gql/graphql";
import {
  CompaniesUpdateInputSchema,
  CompaniesInsertInputSchema,
} from "@/src/gql/validation";

/**
 * Create a new company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CompaniesInsertInput) => {
      // Validate data before database update
      const validatedData = CompaniesInsertInputSchema.parse(input);

      const supabase = getSupabase();

      const { data, error } = await supabase
        .from("companies")
        .insert([validatedData])
        .select()
        .single();

      if (error) {
        console.error("Error creating company:", error);
        throw new Error(error.message);
      }

      return data as Companies;
    },
    retry: 0, // No retries for create operations
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["companies"] });

      // Invalidate technology cache if technologies were provided
      if (data?.current_technologies) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "company-technologies"] });
        // Also invalidate server-side cache
        fetch("/api/tags/invalidate/company-technologies", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side company technologies cache:", error);
        });
      }

      toast.success("Company created successfully");
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      toast.error(errorMessage);
      console.error("Error creating company:", error);
    },
  });
}

/**
 * Update an existing company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: CompaniesUpdateInput }) => {
      // Validate data before database update
      const validatedData = CompaniesUpdateInputSchema.parse(updates);

      const supabase = getSupabase();

      const { data, error } = await supabase
        .from("companies")
        .update({ ...validatedData, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        console.error("Error updating company:", error);
        throw new Error(error.message);
      }

      return data as Companies;
    },
    onMutate: async ({ id, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["companies"] });
      await queryClient.cancelQueries({ queryKey: ["company", id] });
      await queryClient.cancelQueries({ queryKey: ["companies-paginated"] });

      // Snapshot previous values
      const previousCompanies = queryClient.getQueryData<Companies[]>(["companies"]);
      const previousCompany = queryClient.getQueryData<Companies>(["company", id]);
      const previousPaginated = queryClient.getQueryData<{ data: Companies[] }>(["companies-paginated"]);

      // Optimistically update single company cache
      queryClient.setQueryData(["company", id], (old: Companies | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      // Optimistically update companies list
      queryClient.setQueryData(["companies"], (old: Companies[] | undefined) => {
        if (!old) return old;
        return old.map((company) => (company.id === id ? { ...company, ...updates } : company));
      });

      // Optimistically update paginated companies
      queryClient.setQueryData(["companies-paginated"], (old: { data: Companies[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((company: Companies) =>
            company.id === id ? { ...company, ...updates } : company
          ),
        };
      });

      return { previousCompanies, previousCompany, previousPaginated };
    },
    onSuccess: (data: Companies) => {
      // Update caches with server response
      queryClient.setQueryData(["company", data.id], data);

      queryClient.setQueryData(["companies"], (old: Companies[] | undefined) => {
        if (!old) return old;
        return old.map((company) => (company.id === data.id ? data : company));
      });

      queryClient.setQueryData(["companies-paginated"], (old: { data: Companies[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((company: Companies) => (company.id === data.id ? data : company)),
        };
      });

      // Invalidate technology cache if technologies were updated
      if (data?.current_technologies) {
        void queryClient.invalidateQueries({ queryKey: ["tags", "company-technologies"] });
        // Also invalidate server-side cache
        fetch("/api/tags/invalidate/company-technologies", { method: "POST" }).catch((error) => {
          console.error("Failed to invalidate server-side company technologies cache:", error);
        });
      }

      toast.success("Company updated successfully");
    },
    onError: (error, _variables, context: { previousCompanies?: Companies[]; previousCompany?: Companies; previousPaginated?: { data: Companies[] } } | undefined) => {
      // Rollback to previous values
      if (context?.previousCompanies) {
        queryClient.setQueryData(["companies"], context.previousCompanies);
      }
      if (context?.previousCompany) {
        queryClient.setQueryData(["company", _variables.id], context.previousCompany);
      }
      if (context?.previousPaginated) {
        queryClient.setQueryData(["companies-paginated"], context.previousPaginated);
      }

      toast.error("Failed to update company");
      console.error("Error updating company:", error);
    },
  });
}

/**
 * Delete a company
 */
export function useDeleteCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyId: string) => {
      const supabase = getSupabase();

      const { error } = await supabase.from("companies").delete().eq("id", companyId);

      if (error) {
        console.error("Error deleting company:", error);
        throw new Error(error.message);
      }

      return companyId;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["companies"] });

      // Invalidate technology cache when company is deleted
      void queryClient.invalidateQueries({ queryKey: ["tags", "company-technologies"] });
      // Also invalidate server-side cache
      fetch("/api/tags/invalidate/company-technologies", { method: "POST" }).catch((error) => {
        console.error("Failed to invalidate server-side company technologies cache:", error);
      });

      toast.success("Company deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete company");
      console.error("Error deleting company:", error);
    },
  });
}
